import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { getDynamicFormStore } from './useDynamicFormStore';
import DynamicFormFieldDesigner from '../pages/forms/DynamicFormFieldDesigner';
import CoverImageSetting from '../pages/forms/CoverImageSetting';
import PublicAccessPolicyEditor from '../access/PublicAccessPolicyEditor';
import W86SegmentedTabs from '../common/W86SegmentedTabs';
import useTaskInboxStore from '../pages/useTaskInboxStore';
import FlowSettingsBlock from '../FormFlow/FlowSettingsBlock';
import StepperTitleInput from '../FormFlow/StepperTitleInput';
import useUpstreamFlowEntries from '../FormFlow/useUpstreamFlowEntries';
import { NodeSelect, NodeSection } from '../NodeUI';
import './DynamicFormBuilderTemplate.css';

const RESPONSE_MODE_DESCRIPTIONS = {
  FILL_SUBMIT: 'Users fill out the form and submit. No drafts are saved on the server. Each visit starts with a blank form.',
  FILL_REEDIT: 'Users can save drafts and return via a unique URL to continue editing. Drafts are stored on the server and automatically expire after 30 days.',
  VIEW_ONLY: 'Users can view the form but cannot edit or submit responses.',
};

const RESPONSE_MODE_OPTIONS = [
  { value: 'FILL_SUBMIT', label: 'Submit only' },
  { value: 'FILL_REEDIT', label: 'Save & resume drafts' },
  { value: 'VIEW_ONLY', label: 'View only' },
];

const BUILDER_TABS = [
  { value: 0, label: 'Form Design' },
  { value: 1, label: 'Settings' },
];

// Standalone (top-level Form Templates) are served by the template public runtime, which only
// supports PUBLIC/DISABLED today — hide the gated modes it can't serve yet.
const STANDALONE_FORM_ACCESS_MODES = [
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'PUBLIC', label: 'Public' },
];

function FormSettingsPanel({
  data,
  onAccessPolicyChange,
  onResponseModeChange,
  onCoverImageChange,
  activeAccountUsers,
  activeAccountUsersLoading,
  standalone,
  component_id,
  componentName,
  upstreamFlowEntries,
  onFlowSettingsChange,
  onStepperTitleChange,
}) {
  return (
    <div className="dfb-settings-stack">
      <NodeSection title="General">
        <PublicAccessPolicyEditor
          accessPolicy={data.accessPolicy}
          onChange={onAccessPolicyChange}
          users={activeAccountUsers}
          usersLoading={activeAccountUsersLoading}
          modes={standalone ? STANDALONE_FORM_ACCESS_MODES : undefined}
        />
        <NodeSelect
          label="Response mode"
          value={data.responseMode}
          onChange={onResponseModeChange}
          options={RESPONSE_MODE_OPTIONS}
        />
        <p className="dfb-field-hint">
          {RESPONSE_MODE_DESCRIPTIONS[data.responseMode] || ''}
        </p>
      </NodeSection>
      {!standalone && (
        <NodeSection title="Appearance">
          <CoverImageSetting
            value={data.formSettings?.coverImageUrl || ''}
            onChange={onCoverImageChange}
          />
        </NodeSection>
      )}
      {!standalone && (
        <NodeSection title="Flow">
          <FlowSettingsBlock
            componentId={component_id}
            componentName={componentName}
            componentData={data}
            upstreamFlowEntries={upstreamFlowEntries}
            onChange={onFlowSettingsChange}
          />
          <StepperTitleInput
            value={data.stepperTitle}
            onChange={onStepperTitleChange}
          />
        </NodeSection>
      )}
    </div>
  );
}

export default function DynamicFormBuilderTemplate({
  component_id,
  projectId,
  setChildTemplateLoaded,
  availablePlaceholders,
  standalone = false,
  /** When set with standalone, parent owns tab navigation (no inner tab bar). */
  section,
}) {
  const store = useMemo(() => getDynamicFormStore(component_id), [component_id]);
  const data = store((s) => s.data);
  const loaded = store((s) => s.loaded);
  const componentName = store((s) => s.name);

  const [internalTab, setInternalTab] = useState(0);

  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    if (data.accessPolicy?.mode === 'AUTHENTICATED_SELECTED_USERS') {
      fetchActiveAccountUsers();
    }
  }, [data.accessPolicy?.mode, fetchActiveAccountUsers]);

  useEffect(() => {
    if (loaded && setChildTemplateLoaded) setChildTemplateLoaded(true);
  }, [loaded, setChildTemplateLoaded]);

  const handleFieldsChange = useCallback(
    (fields) => store.getState().setFields(fields),
    [store]
  );
  const handleTitleChange = useCallback(
    (val) => store.getState().setTitle(val),
    [store]
  );
  const handleDescriptionChange = useCallback(
    (val) => store.getState().setDescription(val),
    [store]
  );
  const handleAccessPolicyChange = useCallback(
    (val) => store.getState().setAccessPolicy(val),
    [store]
  );
  const handleResponseModeChange = useCallback(
    (val) => store.getState().setResponseMode(val),
    [store]
  );
  const handleCoverImageChange = useCallback(
    (val) => store.getState().setFormSettings({ coverImageUrl: val }),
    [store]
  );
  const handleFlowSettingsChange = useCallback(
    (patch) => store.getState().setFlowSettings(patch),
    [store]
  );
  const handleStepperTitleChange = useCallback(
    (val) => store.getState().setStepperTitle(val),
    [store]
  );

  const upstreamFlowEntries = useUpstreamFlowEntries(component_id);

  if (!loaded) return null;

  const externalSection = standalone && section;
  const showTabBar = !externalSection;
  const activeSection = externalSection || (internalTab === 0 ? 'design' : 'settings');

  // TEMPLATE mode (workflow node bound to a top-level Form Template): fields are owned by the
  // template, so the in-canvas designer is read-only — author edits fields on /forms. Settings
  // (access, flow) remain editable here as workflow-specific overrides.
  const isTemplateMode = !standalone && data.sourceMode === 'TEMPLATE';

  const templateNotice = (
    <div className="dfb-settings-stack">
      <p className="dfb-field-hint">
        This form's fields are defined by a top-level template and resolved at runtime. Edit the
        fields on the Forms page.
      </p>
      {data.formTemplateId && (
        <a
          className="dfb-field-hint"
          href={`/forms/${data.formTemplateId}`}
          target="_blank"
          rel="noreferrer"
        >
          Open template ↗
        </a>
      )}
    </div>
  );

  const designPanel = (
    <DynamicFormFieldDesigner
      fields={data.fields}
      onChange={handleFieldsChange}
      title={data.title}
      description={data.description}
      onTitleChange={handleTitleChange}
      onDescriptionChange={handleDescriptionChange}
      availablePlaceholders={availablePlaceholders}
      surface={standalone ? 'public' : 'classic'}
      coverImageUrl={data.formSettings?.coverImageUrl || ''}
      onCoverImageChange={handleCoverImageChange}
      formId={component_id}
    />
  );

  const settingsPanel = (
    <FormSettingsPanel
      data={data}
      onAccessPolicyChange={handleAccessPolicyChange}
      onResponseModeChange={handleResponseModeChange}
      onCoverImageChange={handleCoverImageChange}
      activeAccountUsers={activeAccountUsers}
      activeAccountUsersLoading={activeAccountUsersLoading}
      standalone={standalone}
      component_id={component_id}
      componentName={componentName}
      upstreamFlowEntries={upstreamFlowEntries}
      onFlowSettingsChange={handleFlowSettingsChange}
      onStepperTitleChange={handleStepperTitleChange}
    />
  );

  return (
    <div className={`dfb-root${standalone ? ' dfb-root--public' : ''}`}>
      {showTabBar && (
        <W86SegmentedTabs
          tabs={BUILDER_TABS}
          value={internalTab}
          onChange={setInternalTab}
          variant="fill"
          ariaLabel="Form sections"
        />
      )}

      {activeSection === 'design' && (isTemplateMode ? templateNotice : designPanel)}
      {activeSection === 'settings' && settingsPanel}
    </div>
  );
}
